import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, linkedSignal, model } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, Subscription } from 'rxjs';
import { filter, map, shareReplay, take } from 'rxjs/operators';

import {
  AlertComponent,
  BaseComponent,
  FormService,
  ModalService,
  NotificationService,
  TabFormModalComponent,
} from 'tms-lib';
import { QuickDespatchOutcome, ShipperBookingValidation } from '../../../models';
import { ShipperInterfaceHeaderService } from '../../../services/edit-create/sales/shipper-interface-header.service';
import { ShipperInterfaceComponent } from '../shipper-interface/shipper-interface.component';

/** What the Shipping Details window closes with when the details pass the checks - book the shipping. */
export const quickShippingDetailsBook = 'book';

/**
 * Quick despatch (QuickDespatchAndBookShipping), shipping = 'Details': the despatch note has been
 * created but its shipping details would fail Book Shipping's checks. The user corrects them on the
 * ordinary shipper interface (packs included, on its Shipment Details grid); Save saves any changes,
 * re-runs the checks, and closes WITH `quickShippingDetailsBook` once they pass, which
 * QuickDespatchHandlerService books. Back dismisses it, and the handler prints without booking.
 *
 * ⚠️ ng-bootstrap renders the modal BEFORE the caller can set `outcome` (ui.md, "Opening a modal"),
 * so the shipper interface is gated on it rather than created with no id.
 */
@Component({
  selector: 'app-shipping-details-quick-modal',
  templateUrl: './shipping-details-quick-modal.component.html',
  styleUrl: './shipping-details-quick-modal.component.scss',
  imports: [TabFormModalComponent, AlertComponent, ShipperInterfaceComponent],
})
export class ShippingDetailsQuickModalComponent extends BaseComponent {
  private readonly shipperInterfaceHeaderService = inject(ShipperInterfaceHeaderService);
  private readonly formService = inject(FormService);
  private readonly modalService = inject(ModalService);
  private readonly notificationService = inject(NotificationService);

  public readonly outcome = model<QuickDespatchOutcome>();

  // The API's errors to start with, replaced by each re-check. Already translated by the API.
  protected readonly errors = linkedSignal<string[]>(() => this.outcome()?.verificationErrors ?? []);

  // Stable field (not a getter), carrying the despatch note's real id for app-summary-top.
  protected readonly model$: Observable<{ id: string }> = toObservable(this.outcome).pipe(
    filter((outcome: QuickDespatchOutcome) => !!outcome?.despatchNoteId),
    map((outcome: QuickDespatchOutcome) => ({ id: outcome.despatchNoteId })),
    shareReplay(1),
  );

  private saveSubscription: Subscription;
  private verifying = false;

  protected save() {
    if (this.verifying) {
      return;
    }

    // The shipper interface's form sits at this modal's level. Save its changes first, the way
    // the shipper interface's own Book Shipping does, and check once the save has completed. An
    // invalid form never completes, so the user stays here with the form's own messages.
    const modalLevel = this.modalService.modalLevel;
    if (this.formService.anyFormChanges(modalLevel)) {
      this.saveSubscription?.unsubscribe();
      this.saveSubscription = this.formService.saveComplete
        .pipe(take(1), this.untilDestroyed())
        .subscribe(() => this.verify());
      this.formService.save.next(modalLevel);
    } else {
      this.verify();
    }
  }

  private verify() {
    const shipperInterfaceHeaderId = this.outcome()?.shipperInterfaceHeaderId;
    if (!shipperInterfaceHeaderId) {
      return;
    }

    this.verifying = true;
    this.shipperInterfaceHeaderService
      .validateBooking(shipperInterfaceHeaderId)
      .pipe(this.untilDestroyed())
      .subscribe({
        next: (validation: ShipperBookingValidation) => {
          this.verifying = false;
          if (validation?.messages?.length) {
            this.errors.set(validation.messages);
            return;
          }
          this.modalService.close(quickShippingDetailsBook);
        },
        error: (error: HttpErrorResponse) => {
          this.verifying = false;
          this.notificationService.apiFail(error, $localize`The shipping details could not be checked.`);
        },
      });
  }
}
